import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Wifi, WifiOff, Printer, Settings, Check, RefreshCw, Download, ChevronDown } from 'lucide-react';
import { getAgentConfig, setAgentConfig, clearAgentConfig, isAgentConfigured, syncFromCloud, saveToCloud, autoPair, getStoreAuthHeaders } from '../utils/thermalPrint';

const AGENT_URL = 'https://kangavena.com/downloads/KangavenaPrintAgent-v7.5.1.exe?v=7.5.1';
const POLL = 10000;

export default function PrinterSetupModal({ isOpen, onClose, storeApiKey }: { isOpen: boolean; onClose: () => void; storeApiKey?: string }) {
  const [selPrinter, setSelPrinter] = useState('');
  const [printers, setPrinters] = useState<string[]>([]);
  const [pendingAgents, setPendingAgents] = useState<Array<{ agentId: string; ip: string; printers: any[] }>>([]);
  const [status, setStatus] = useState<'idle' | 'checking' | 'online' | 'offline'>('idle');
  const [saved, setSaved] = useState(false);

  const doCheck = useCallback(async (isSilent = false) => {
    if (!isSilent) setStatus('checking');
    try {
      // Try server API first with resilient credentials (works from any device)
      const authHeaders = getStoreAuthHeaders(storeApiKey);
      if (Object.keys(authHeaders).length > 0) {
        const res = await fetch('/api/printer/setup', {
          headers: authHeaders,
          signal: AbortSignal.timeout(5000)
        });
        if (res.ok) {
          const data = await res.json();
          if (data.pendingAgents) setPendingAgents(data.pendingAgents);
          if (data.success && data.agentConnected) {
            const ap: string[] = (data.printers || []).map((p: any) => p.name || p.connectionAddress).filter(Boolean);
            setStatus('online');
            setPrinters(ap);
            const cfg = getAgentConfig();
            if (cfg.defaultPrinter && ap.includes(cfg.defaultPrinter)) {
              setSelPrinter(cfg.defaultPrinter);
            } else if (ap.length > 0) {
              const defaultP = (data.printers || []).find((p: any) => p.isDefault)?.name || ap[0];
              setSelPrinter(defaultP);
            }
            return;
          }
        }
      }

      // Fallback: direct LAN (same PC only — try 127.0.0.1 then localhost)
      for (const host of ['127.0.0.1', 'localhost']) {
        try {
          const res = await fetch(`http://${host}:9123/status`, { signal: AbortSignal.timeout(1200) });
          if (res.ok) {
            const d = await res.json();
            if (d.status === 'online') {
              const ap: string[] = d.printers || [];
              setStatus('online'); setPrinters(ap);
              const cfg = getAgentConfig();
              if (cfg.defaultPrinter && ap.includes(cfg.defaultPrinter)) setSelPrinter(cfg.defaultPrinter);
              else if (ap.length > 0) setSelPrinter(prev => prev || ap[0]);
              return;
            }
          }
        } catch {}
      }
    } catch {}
    setStatus('offline');
  }, [storeApiKey]);

  const handleClaim = async (agentId: string) => {
    try {
      const authHeaders = getStoreAuthHeaders(storeApiKey);
      const res = await fetch('/api/printer/claim-agent', {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ agentId })
      });
      if (res.ok) {
        doCheck(false);
      }
    } catch {}
  };

  useEffect(() => {
    if (!isOpen) return;
    setSaved(false); setPrinters([]); setStatus('idle');
    syncFromCloud().then(cfg => { if (cfg.defaultPrinter) setSelPrinter(cfg.defaultPrinter); }).catch(() => {});
    doCheck(false);
    const id = setInterval(() => doCheck(true), POLL);
    return () => clearInterval(id);
  }, [isOpen, doCheck]);

  const handleSave = useCallback(() => {
    const token = storeApiKey || getAgentConfig().secret || '';
    setAgentConfig('127.0.0.1', token, selPrinter);
    saveToCloud('127.0.0.1', token, selPrinter);
    setSaved(true);
  }, [selPrinter, storeApiKey]);

  const handleClear = useCallback(() => {
    clearAgentConfig(); setSelPrinter(''); setPrinters([]); setStatus('idle'); setSaved(false);
  }, []);

  if (!isOpen) return null;

  const dot = status === 'online' ? 'bg-emerald-500' : status === 'offline' ? 'bg-red-500' : status === 'checking' ? 'bg-amber-400 animate-pulse' : 'bg-slate-300';
  const label = status === 'online' ? 'Connected' : status === 'offline' ? 'Offline' : status === 'checking' ? 'Checking...' : 'Not configured';

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center"><Printer size={18} className="text-white" /></div>
              <div><h2 className="text-sm font-black text-slate-800">Printer Setup</h2><p className="text-[10px] text-slate-500">Smart Print Agent v7.4 (XP-470B 4-Inch & 80mm/58mm)</p></div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-50 border border-slate-100">
                  <div className={`w-2 h-2 rounded-full ${dot}`} /><span className="text-[9px] font-bold text-slate-600">{label}</span>
                </div>
                <button onClick={() => doCheck(false)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors" title="Refresh">
                  <RefreshCw size={12} className={`text-slate-400 ${status === 'checking' ? 'animate-spin' : ''}`} />
                </button>
              </div>
              <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100"><X size={16} className="text-slate-400" /></button>
            </div>
          </div>

          {/* Body */}
          <div className="p-5 space-y-4">
            {status === 'online' && (
              <div className="flex items-center gap-2 p-2.5 rounded-xl bg-emerald-50 border border-emerald-200">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /><p className="text-[11px] font-bold text-emerald-700">Agent connected — select your printer below</p>
              </div>
            )}

            {/* Pending detected agents ready for 1-click claim */}
            {pendingAgents.length > 0 && (
              <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                    <p className="text-[11px] font-black text-amber-800">Detected Waiting Print Agent:</p>
                  </div>
                </div>
                {pendingAgents.map(ag => (
                  <div key={ag.agentId} className="flex items-center justify-between bg-white p-2.5 rounded-lg border border-amber-200">
                    <div>
                      <p className="text-[11px] font-mono font-bold text-slate-800">{ag.agentId}</p>
                      <p className="text-[9px] text-slate-500">{ag.printers?.length || 0} printers detected (XP-470B / POS)</p>
                    </div>
                    <button
                      onClick={() => handleClaim(ag.agentId)}
                      className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-[10px] shadow-sm flex items-center gap-1 transition-all"
                    >
                      <Check size={12} /> {status === 'online' ? 'Switch to This Agent' : 'Connect This Agent'}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {status === 'offline' && pendingAgents.length === 0 && (
              <div className="flex items-center gap-2 p-2.5 rounded-xl bg-red-50 border border-red-200">
                <WifiOff size={14} className="text-red-500 shrink-0" /><p className="text-[11px] font-bold text-red-700">Agent offline — download and run the agent on the printer PC</p>
              </div>
            )}

            {/* Printer selector */}
            {status === 'online' && printers.length > 0 && (
              <div>
                <label className="block text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1.5">Default Printer</label>
                <div className="relative">
                  <select value={selPrinter} onChange={e => {
                    setSelPrinter(e.target.value);
                    const cfg = getAgentConfig();
                    setAgentConfig(cfg.ip || '127.0.0.1', cfg.secret || '', e.target.value);
                    saveToCloud('127.0.0.1', cfg.secret || '', e.target.value);
                  }} className="w-full px-3 py-2.5 pr-8 rounded-xl border border-slate-200 bg-slate-50 text-sm text-slate-800 appearance-none focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-400 cursor-pointer">
                    {printers.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
                </div>
              </div>
            )}

            {/* Setup guide */}
            <div className="p-3 rounded-xl bg-blue-50 border border-blue-100">
              <div className="flex items-start gap-2">
                <Settings size={14} className="text-blue-500 mt-0.5 shrink-0" />
                <div className="text-[10px] text-blue-700 space-y-2">
                  <p className="font-bold">Setup Guide</p>
                  <ol className="list-decimal list-inside space-y-1 text-blue-600">
                    <li>Download & run the agent on the printer PC</li>
                    <li className="ml-4"><a href={AGENT_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 text-white font-bold text-[10px] hover:bg-indigo-700 shadow-sm"><Download size={12} /> Download Print Agent v7.4 (XP-470B Ready)</a></li>
                    <li>Select your printer below and click Save</li>
                  </ol>
                  <p className="text-[9px] text-blue-500 mt-1">Pairing is automatic — no secret needed.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center gap-2 p-5 pt-0">
            {isAgentConfigured() && <button onClick={handleClear} className="px-4 py-2.5 rounded-xl text-[11px] font-bold text-red-600 bg-red-50 hover:bg-red-100">Disconnect</button>}
            <div className="flex-1" />
            <button onClick={onClose} className="px-4 py-2.5 rounded-xl text-[11px] font-bold text-slate-600 bg-slate-100 hover:bg-slate-200">Cancel</button>
            <button onClick={handleSave} disabled={status !== 'online'} className="px-5 py-2.5 rounded-xl text-[11px] font-black text-white bg-gradient-to-r from-indigo-500 to-purple-600 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/25">
              {saved ? <span className="flex items-center gap-1"><Check size={12} /> Saved</span> : 'Save'}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
